import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowRight, Flag, Info, Pencil, Plus, Trash2, TriangleAlert } from 'lucide-react';
import {
  getGameContent, addGameNode, updateGameNode, deleteGameNode,
  addNodeChoice, updateNodeChoice, deleteNodeChoice,
} from '../../api/games';
import errorText from '../../utils/errorText';
import Dropdown from '../../components/Dropdown/Dropdown';
import { useConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import toast from 'react-hot-toast';
import { useI18n } from '../../i18n/I18nContext';
import { pluralKey } from '../../utils/plural';

const BLANK_STEP = { type: 'PROMPT', text: '', scoreOnReach: 0 };
const MAX_CHOICES = 4;
const KNOWN_CODES = ['START_NODE_REQUIRED', 'TERMINAL_HAS_CHOICES'];

/** The API's reason when it has one we word ourselves, else the caller's fallback. */
export function storyErrorText(err, t, fallback) {
  const code = err?.error?.code;
  if (KNOWN_CODES.includes(code)) return t(`gameAuthoring.games.errorCode.${code}`);
  return errorText(err, fallback);
}

const shorten = (text, n = 40) => (text.length > n ? `${text.slice(0, n)}…` : text);

function StepForm({ initial, saving, submitLabel, onSubmit, onCancel }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ ...BLANK_STEP, ...initial });
  const valid = form.text.trim();

  return (
    <form
      className="cg-form cg-form--inset"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !saving) onSubmit({ type: form.type, text: form.text.trim(), scoreOnReach: Number(form.scoreOnReach) || 0 });
      }}
    >
      <div className="cg-node-form-row">
        <Dropdown
          value={form.type}
          onChange={(val) => setForm((f) => ({ ...f, type: val }))}
          options={[
            { value: 'PROMPT', label: t('courseAssess.games.nodeType.PROMPT') },
            { value: 'TERMINAL', label: t('courseAssess.games.nodeType.TERMINAL') },
          ]}
          ariaLabel={t('courseAssess.games.nodeTypeLabel')}
        />
        <span className="cg-score">
          <input
            className="cg-input cg-input-score"
            type="number"
            min={0}
            placeholder={t('courseAssess.games.pointsPlaceholder')}
            aria-label={t('courseAssess.games.pointsPlaceholder')}
            value={form.scoreOnReach}
            onChange={(e) => setForm((f) => ({ ...f, scoreOnReach: e.target.value }))}
            disabled={saving}
          />
          <span className="cg-score-unit" aria-hidden="true">{t('courseAssess.games.pointsSuffix')}</span>
        </span>
      </div>
      <textarea
        className="cg-textarea"
        rows={3}
        maxLength={2000}
        data-autofocus
        placeholder={t('courseAssess.games.nodeTextPlaceholder')}
        aria-label={t('courseAssess.games.nodeTextPlaceholder')}
        value={form.text}
        onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
        disabled={saving}
      />
      <div className="cg-form-actions">
        <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={onCancel} disabled={saving}>{t('courseAssess.games.cancel')}</button>
        <button type="submit" className={`ds-btn ds-btn-primary ds-btn-sm ${saving ? 'is-loading' : ''}`} disabled={saving || !valid}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function ChoiceForm({ initial, targets, saving, submitLabel, onSubmit, onCancel }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ label: '', toNodeId: '', ...initial });
  const valid = form.label.trim() && form.toNodeId;

  return (
    <form
      className="cg-choice-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !saving) onSubmit({ label: form.label.trim(), toNodeId: form.toNodeId });
      }}
    >
      <input
        className="cg-input cg-input-grow"
        type="text"
        maxLength={500}
        data-autofocus
        placeholder={t('courseAssess.games.choiceLabelPlaceholder')}
        aria-label={t('courseAssess.games.choiceLabelPlaceholder')}
        value={form.label}
        onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
        disabled={saving}
      />
      <Dropdown
        value={form.toNodeId}
        onChange={(val) => setForm((f) => ({ ...f, toNodeId: val }))}
        options={[{ value: '', label: t('courseAssess.games.leadsToPlaceholder') }, ...targets]}
        ariaLabel={t('courseAssess.games.leadsToPlaceholder')}
      />
      <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={onCancel} disabled={saving}>{t('courseAssess.games.cancel')}</button>
      <button type="submit" className={`ds-btn ds-btn-primary ds-btn-sm ${saving ? 'is-loading' : ''}`} disabled={saving || !valid}>
        {submitLabel}
      </button>
    </form>
  );
}

/**
 * A story game's saved steps, read back from the API, each with its choices: add, edit and
 * delete steps and choices, move the first step. `onStats` hears the step count and whether
 * a first step exists (the parent only lets a complete story be shown to students).
 */
export default function StoryEditor({ game, onStats }) {
  const { t, lang } = useI18n();
  const [confirm, confirmDialog] = useConfirmDialog();
  const [status, setStatus] = useState('loading');
  const [nodes, setNodes] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [choiceFor, setChoiceFor] = useState(null);
  const [editingChoice, setEditingChoice] = useState(null);
  const [busy, setBusy] = useState(null);
  const statsRef = useRef(onStats);
  statsRef.current = onStats;

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const content = await getGameContent(game.id);
      setNodes(content.nodes || []);
      setStatus('ready');
    } catch {
      setStatus('failed');
    }
  }, [game.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (status === 'ready') statsRef.current?.({ nodes: nodes.length, hasStart: nodes.some((n) => n.isStartNode) });
  }, [status, nodes]);

  const numberOf = new Map(nodes.map((n, i) => [n.id, i + 1]));
  const stepName = (id) => (numberOf.has(id) ? t('gameAuthoring.games.stepNumber', { n: numberOf.get(id) }) : t('gameAuthoring.games.missingTarget'));
  const targets = nodes.map((n) => ({ value: n.id, label: `${stepName(n.id)} · ${shorten(n.text, 30)}` }));
  const hasStart = nodes.some((n) => n.isStartNode);

  const run = async (key, fn, failKey) => {
    setBusy(key);
    try {
      await fn();
      return true;
    } catch (err) {
      toast.error(storyErrorText(err, t, t(failKey)));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const handleAddStep = (payload) => run('new', async () => {
    // The first step (or the first one after the start was deleted) is the start
    const created = await addGameNode(game.id, { ...payload, isStartNode: !hasStart });
    setNodes((prev) => [...prev, { ...created, choices: [] }]);
    setAdding(false);
  }, 'courseAssess.games.addNodeFailed');

  const handleSaveStep = (node, payload) => run(node.id, async () => {
    const updated = await updateGameNode(game.id, node.id, payload);
    setNodes((prev) => prev.map((n) => (n.id === node.id ? { ...n, ...updated, choices: updated.choices || n.choices } : n)));
    setEditingId(null);
    toast.success(t('gameAuthoring.games.saved'));
  }, 'gameAuthoring.games.saveFailed');

  const handleMakeStart = (node) => run(node.id, async () => {
    await updateGameNode(game.id, node.id, { isStartNode: true });
    setNodes((prev) => prev.map((n) => ({ ...n, isStartNode: n.id === node.id })));
  }, 'gameAuthoring.games.saveFailed');

  const handleDeleteStep = async (node) => {
    const touching = (node.choices || []).length
      + nodes.filter((n) => n.id !== node.id).reduce((sum, n) => sum + (n.choices || []).filter((c) => c.toNodeId === node.id).length, 0);
    const parts = [];
    if (touching > 0) parts.push(t(pluralKey('gameAuthoring.games.deleteStepDesc', touching, lang), { count: touching }));
    if (node.isStartNode && nodes.length > 1) parts.push(t('gameAuthoring.games.deleteStepStart'));
    const ok = await confirm({
      title: t('gameAuthoring.games.deleteStepTitle'),
      description: parts.join(' ') || undefined,
      confirmLabel: t('courseAssess.games.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    await run(node.id, async () => {
      const result = await deleteGameNode(game.id, node.id);
      const startId = result?.startNodeId ?? null;
      setNodes((prev) => prev
        .filter((n) => n.id !== node.id)
        .map((n) => ({
          ...n,
          isStartNode: startId ? n.id === startId : n.isStartNode,
          choices: (n.choices || []).filter((c) => c.toNodeId !== node.id),
        })));
      if (editingId === node.id) setEditingId(null);
      toast.success(t('gameAuthoring.games.deleted'));
    }, 'gameAuthoring.games.deleteFailed');
  };

  const handleAddChoice = (node, payload) => run(`choice-new-${node.id}`, async () => {
    const created = await addNodeChoice(game.id, node.id, payload);
    setNodes((prev) => prev.map((n) => (n.id === node.id ? { ...n, choices: [...(n.choices || []), created] } : n)));
    setChoiceFor(null);
  }, 'courseAssess.games.addChoiceFailed');

  const handleSaveChoice = (node, choice, payload) => run(choice.id, async () => {
    const updated = await updateNodeChoice(game.id, choice.id, payload);
    setNodes((prev) => prev.map((n) => (n.id === node.id
      ? { ...n, choices: n.choices.map((c) => (c.id === choice.id ? { ...c, ...updated } : c)) }
      : n)));
    setEditingChoice(null);
  }, 'gameAuthoring.games.saveFailed');

  const handleDeleteChoice = async (node, choice) => {
    const ok = await confirm({
      title: t('gameAuthoring.games.deleteChoiceTitle'),
      description: `"${choice.label}"`,
      confirmLabel: t('courseAssess.games.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    await run(choice.id, async () => {
      await deleteNodeChoice(game.id, choice.id);
      setNodes((prev) => prev.map((n) => (n.id === node.id ? { ...n, choices: n.choices.filter((c) => c.id !== choice.id) } : n)));
    }, 'gameAuthoring.games.deleteFailed');
  };

  if (status === 'loading') {
    return <p className="cg-empty-hint">{t('courseAssess.games.loadingGames')}</p>;
  }
  if (status === 'failed') {
    return (
      <div className="ui-alert" data-tone="danger" role="alert">
        <TriangleAlert aria-hidden="true" />
        <p>{t('gameAuthoring.games.contentLoadFailed')}</p>
        <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={load}>{t('ui.retry')}</button>
      </div>
    );
  }

  return (
    <div className="cg-editor-inner">
      <p className="cg-editor-help"><Info size={14} aria-hidden="true" /> {t('gameAuthoring.games.storyHelp')}</p>

      <div className="cg-editor-toolbar">
        <span className="cg-editor-count">{t('courseAssess.games.nodesCount', { count: nodes.length })}</span>
        {!adding && (
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => { setAdding(true); setEditingId(null); }}>
            <Plus aria-hidden="true" />
            {t('courseAssess.games.addNode')}
          </button>
        )}
      </div>

      {nodes.length > 0 && !hasStart && (
        <div className="ui-alert" data-tone="warning" role="status">
          <TriangleAlert aria-hidden="true" />
          <p>{t('gameAuthoring.games.noStart')}</p>
        </div>
      )}

      {nodes.length === 0 && !adding && (
        <p className="cg-empty-hint">{t('courseAssess.games.noNodesHint')}</p>
      )}

      <div className="cg-node-list">
        {nodes.map((node) => {
          const terminal = node.type === 'TERMINAL';
          const choices = node.choices || [];
          return (
            <div key={node.id} className={`cg-node ${node.isStartNode ? 'is-start' : ''}`}>
              <div className="cg-node-header">
                <span className="cg-step-num">{stepName(node.id)}</span>
                <span className={`ds-badge ${terminal ? 'ds-badge-danger' : 'ds-badge-info'}`}>{t(`courseAssess.games.nodeType.${node.type}`)}</span>
                {node.isStartNode && (
                  <span className="ds-badge ds-badge-success"><Flag size={12} aria-hidden="true" /> {t('courseAssess.games.startBadge')}</span>
                )}
                <span className="cg-node-score">+{node.scoreOnReach} {t('courseAssess.games.pointsSuffix')}</span>
                <span className="cg-node-actions">
                  {/* A story cannot start on its ending */}
                  {!node.isStartNode && !terminal && (
                    <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm" onClick={() => handleMakeStart(node)} disabled={busy === node.id}>
                      <Flag aria-hidden="true" />
                      {t('gameAuthoring.games.makeStart')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="ui-icon-btn"
                    onClick={() => { setEditingId(node.id); setAdding(false); }}
                    aria-label={`${t('gameAuthoring.games.edit')}: ${stepName(node.id)}`}
                    title={t('gameAuthoring.games.edit')}
                  >
                    <Pencil aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="ui-icon-btn ui-icon-btn--danger"
                    onClick={() => handleDeleteStep(node)}
                    disabled={busy === node.id}
                    aria-label={`${t('courseAssess.games.delete')}: ${stepName(node.id)}`}
                    title={t('courseAssess.games.delete')}
                  >
                    <Trash2 aria-hidden="true" />
                  </button>
                </span>
              </div>

              {editingId === node.id ? (
                <StepForm
                  initial={{ type: node.type, text: node.text, scoreOnReach: node.scoreOnReach }}
                  saving={busy === node.id}
                  submitLabel={t('gameAuthoring.games.save')}
                  onCancel={() => setEditingId(null)}
                  onSubmit={(payload) => handleSaveStep(node, payload)}
                />
              ) : (
                <p className="cg-node-text">{node.text}</p>
              )}

              {choices.length > 0 && (
                <div className="cg-choice-list">
                  {choices.map((c) => (
                    editingChoice === c.id ? (
                      <ChoiceForm
                        key={c.id}
                        initial={{ label: c.label, toNodeId: c.toNodeId }}
                        targets={targets}
                        saving={busy === c.id}
                        submitLabel={t('gameAuthoring.games.save')}
                        onCancel={() => setEditingChoice(null)}
                        onSubmit={(payload) => handleSaveChoice(node, c, payload)}
                      />
                    ) : (
                      <div key={c.id} className="cg-choice-item">
                        <ArrowRight className="cg-choice-arrow" size={14} aria-hidden="true" />
                        <span className="cg-choice-label">"{c.label}"</span>
                        <span className="cg-choice-target">{t('gameAuthoring.games.choiceTarget', { step: stepName(c.toNodeId) })}</span>
                        <span className="cg-choice-actions">
                          <button
                            type="button"
                            className="ui-icon-btn"
                            onClick={() => setEditingChoice(c.id)}
                            aria-label={`${t('gameAuthoring.games.edit')}: ${c.label}`}
                            title={t('gameAuthoring.games.edit')}
                          >
                            <Pencil aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="ui-icon-btn ui-icon-btn--danger"
                            onClick={() => handleDeleteChoice(node, c)}
                            disabled={busy === c.id}
                            aria-label={`${t('courseAssess.games.delete')}: ${c.label}`}
                            title={t('courseAssess.games.delete')}
                          >
                            <Trash2 aria-hidden="true" />
                          </button>
                        </span>
                      </div>
                    )
                  ))}
                </div>
              )}

              {terminal && <p className="cg-node-note">{t('gameAuthoring.games.endingNote')}</p>}
              {!terminal && choices.length === 0 && (
                <p className="cg-node-warn"><TriangleAlert size={14} aria-hidden="true" /> {t('gameAuthoring.games.deadEnd')}</p>
              )}

              {!terminal && choices.length < MAX_CHOICES && (
                choiceFor === node.id ? (
                  <ChoiceForm
                    targets={targets}
                    saving={busy === `choice-new-${node.id}`}
                    submitLabel={t('courseAssess.games.add')}
                    onCancel={() => setChoiceFor(null)}
                    onSubmit={(payload) => handleAddChoice(node, payload)}
                  />
                ) : (
                  <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm cg-add-choice" onClick={() => setChoiceFor(node.id)}>
                    <Plus aria-hidden="true" />
                    {t('courseAssess.games.addChoice')}
                  </button>
                )
              )}
            </div>
          );
        })}
      </div>

      {adding && (
        <StepForm
          initial={BLANK_STEP}
          saving={busy === 'new'}
          submitLabel={t('courseAssess.games.add')}
          onCancel={() => setAdding(false)}
          onSubmit={handleAddStep}
        />
      )}
      {confirmDialog}
    </div>
  );
}
